import type { BarSeriesOption } from 'echarts/charts';
import type { DatasetOption } from 'echarts/types/dist/shared';

import { DatasetBase } from '@core/reports-types';
import { NET_CHANGE_NEGATIVE_COLUMN, NET_CHANGE_POSITIVE_COLUMN } from '@core/reports-util';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';

const STACK = 'net-change';

const toNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number(value ?? 0);

export interface NetChangeBars {
  dataset: DatasetOption;
  series: BarSeriesOption[];
}

/**
 * Builds the two diverging bar series of a net change chart out of the positive
 * and negative value columns. The negative metric is flipped below (vertical)
 * or left of (horizontal) the zero baseline and both series share a stack so
 * they anchor at 0. The net column stays out of the chart - it is surfaced in
 * the tooltip, table and summary instead.
 *
 * The two series are always emitted in a fixed order so the echarts palette
 * maps deterministically: positive -> palette[0] (`colorChartPrimaryDefault`),
 * negative -> palette[1] (`colorChartPurpleDefault`). No explicit colors are
 * set here.
 */
export const buildNetChangeBars = (
  dataset: DatasetBase,
  orientation: 'vertical' | 'horizontal',
): NetChangeBars => {
  const [indexDimension, positive, negative] = dataset.dataset.dimensions;

  if (!indexDimension || !positive || !negative) {
    return { dataset: dataset.dataset, series: [] };
  }

  const source = dataset.dataset.source.map(row => [
    row[0],
    toNumber(row[NET_CHANGE_POSITIVE_COLUMN]),
    -toNumber(row[NET_CHANGE_NEGATIVE_COLUMN]),
  ]);

  const encode = (valueIndex: number) =>
    orientation === 'vertical'
      ? { x: indexDimension.id, y: valueIndex }
      : { x: valueIndex, y: indexDimension.id };

  const series = [positive, negative].map(
    (dimension, index): BarSeriesOption => ({
      id: dimension.id,
      name: dimension.name,
      type: 'bar',
      seriesLayoutBy: 'column',
      stack: STACK,
      encode: encode(index + 1),
      emphasis: { focus: 'series' },
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
    }),
  );

  return {
    dataset: <DatasetOption>{
      dimensions: [indexDimension, positive, negative],
      source,
    },
    series,
  };
};
